/**
 * The recording in progress, held outside React so it survives in-app navigation.
 *
 * A recording usually crosses screens — open a list, click into a record, fill a form —
 * and every route change remounts the page underneath. Module state is the same choice
 * `gms-adapter.ts` makes for its one active walkthrough, for the same reason: there is
 * only ever one, and every part of GMS needs to see it without a provider.
 *
 * A full page reload restores it (`recorder-persist.ts`): the step list from this tab's
 * `sessionStorage`, the screenshots from IndexedDB, for the same signed-in user only.
 * Restoring is lazy — `restoreRecording()` runs when the recorder mounts, which happens
 * only for authors in the rollout — so nobody else's page load touches IndexedDB.
 */

import { useSyncExternalStore } from 'react';

import type { RecordedLocator } from '../types';
import {
  clearRecording,
  currentOwner,
  loadRecording,
  loadScreenshot,
  newRecordingId,
  saveRecording,
  saveScreenshot,
  sweepStaleScreenshots,
} from './recorder-persist';

export type RecorderStatus = 'idle' | 'confirming' | 'recording' | 'finishing';

export interface RecordedStep {
  key: string;
  text: string;
  locator: RecordedLocator;
  /** Resolves to the step's screenshot, or null when it could not be taken. */
  screenshot: Promise<File | null>;
}

export interface RecorderState {
  /** Identifies this recording's saved screenshots; empty while idle. */
  id: string;
  status: RecorderStatus;
  steps: RecordedStep[];
  /** Where recording started: the screen the guide belongs to. */
  startRoute: string;
  startScreenName: string;
  /** Clicks left out because nothing on the element could find it again. */
  unrecordable: number;
}

const IDLE: RecorderState = {
  id: '',
  status: 'idle',
  steps: [],
  startRoute: '',
  startScreenName: '',
  unrecordable: 0,
};

/** The recording this tab was in the middle of before a reload, if any. */
function restored(): RecorderState {
  sweepStaleScreenshots();
  const saved = loadRecording();
  if (!saved) return IDLE;
  return {
    id: saved.id,
    status: saved.status,
    startRoute: saved.startRoute,
    startScreenName: saved.startScreenName,
    unrecordable: 0,
    steps: saved.steps.map((step) => ({
      ...step,
      screenshot: loadScreenshot(saved.id, step.key),
    })),
  };
}

let state: RecorderState = IDLE;
let restoreAttempted = false;
const listeners = new Set<() => void>();

function set(next: RecorderState): void {
  state = next;
  if (next.status === 'recording' || next.status === 'finishing') {
    saveRecording({
      id: next.id,
      owner: currentOwner(),
      status: next.status,
      startRoute: next.startRoute,
      startScreenName: next.startScreenName,
      steps: next.steps.map(({ key, text, locator }) => ({ key, text, locator })),
      savedAt: Date.now(),
    });
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Pick up a recording interrupted by a reload, and sweep stale screenshots. Called by the
 * recorder on mount (authors only); does its work once per page load.
 */
export function restoreRecording(): void {
  if (restoreAttempted) return;
  restoreAttempted = true;
  if (state.status !== 'idle') return;
  const previous = restored();
  if (previous.status !== 'idle') set(previous);
}

/** The current recording. Re-renders on every change. */
export function useRecorder(): RecorderState {
  return useSyncExternalStore(subscribe, () => state);
}

export function getRecorderState(): RecorderState {
  return state;
}

/** Ask the author to confirm they understand they are recording live data. */
export function requestRecording(): void {
  if (state.status === 'idle') set({ ...IDLE, status: 'confirming' });
}

export function startRecording(route: string, screenName: string): void {
  set({
    id: newRecordingId(),
    status: 'recording',
    steps: [],
    startRoute: route,
    startScreenName: screenName,
    unrecordable: 0,
  });
}

/** A click the recorder had to leave out; shown on the banner so it is not silent. */
export function noteUnrecordable(): void {
  if (state.status === 'recording') set({ ...state, unrecordable: state.unrecordable + 1 });
}

export function addRecordedStep(step: Omit<RecordedStep, 'key'>): void {
  if (state.status !== 'recording') return;
  const key = `step-${state.steps.length + 1}`;
  const recordingId = state.id;
  set({ ...state, steps: [...state.steps, { ...step, key }] });
  // Kept for a reload — unless the recording ended while the picture was rendering.
  void step.screenshot.then((file) => {
    if (file && state.id === recordingId) saveScreenshot(recordingId, key, file);
  });
}

/** Stop capturing and move to naming the guide. */
export function stopRecording(): void {
  if (state.status === 'recording') set({ ...state, status: 'finishing' });
}

/** Back to recording from the naming dialog. */
export function resumeRecording(): void {
  if (state.status === 'finishing') set({ ...state, status: 'recording' });
}

/**
 * End the recording and delete everything kept for it in the browser — called on
 * Discard and after a successful upload. Nothing has left the browser until upload.
 */
export function discardRecording(): void {
  const { id } = state;
  set(IDLE);
  clearRecording(id);
}
